import DOMPurify from 'dompurify';
import React from 'react';
import parse, { DOMNode, Element, HTMLReactParserOptions, domToReact } from 'html-react-parser';
import { TableCellHeaderStates, TableCellNode, TableNode, TableRowNode } from '@lexical/table';

import { PhxParagraphNode } from 'components/ContentEditor/nodes/PhxParagraphNode';
import { PhxTextNode } from 'components/ContentEditor/nodes/PhxTextNode';
import { getListMarker, isValidListType } from 'components/ContentEditor/nodes/PhxListNode/listStyles';

import {
  getSanitizedDocumentFromString,
  removeIssuedDeletedNodes,
  syncDocumentContent,
  unWrapIssuedInsertedNodes,
} from './documentTransformations';
import { pipe } from 'utils/functional';

import { defaultClassToStyleMap } from './classToStyleMap';
import {
  defaultAllowedTags,
  defaultAllowedAttributes,
  createEditorSanitizeConfig,
  createRenderSanitizeConfig,
} from './sanitizationConfig';
import { applyCommonCleanup, removeLeadingEmptyParagraph, shouldPreserveClass } from './commonDOMOperations';

import type { ClassToStyleMap } from './classToStyleMap';
import type { AllowedAttributes } from './sanitizationConfig';

// Whitelist for inline `style=""` declarations that we keep during editor conversion.
// This prevents pasted MS Word styles (mso-*) and other unexpected layout/CSS from being preserved.
const ALLOWED_INLINE_STYLE_PROPERTIES = new Set<string>([
  'font-weight',
  'font-style',
  'text-decoration',
  'color',
  // Support common shorthand used in existing content fixtures.
  'background',
  'background-color',
  'list-style-type',
  // Used for editor tables (OnPastePlugin reads `col.style.width`)
  'width',
  'height',
  'vertical-align',
]);

const filterAllowedInlineStyleProperties = (style: string): string => {
  if (!style) return '';

  const parseHexColor = (hex: string): { r: number; g: number; b: number } | null => {
    const normalized = hex.trim().toLowerCase();
    if (!normalized.startsWith('#')) return null;

    const raw = normalized.slice(1);
    if (![3, 6].includes(raw.length)) return null;

    const toByte = (s: string) => parseInt(s, 16);

    if (raw.length === 3) {
      return {
        r: toByte(raw[0] + raw[0]),
        g: toByte(raw[1] + raw[1]),
        b: toByte(raw[2] + raw[2]),
      };
    }

    return {
      r: toByte(raw.slice(0, 2)),
      g: toByte(raw.slice(2, 4)),
      b: toByte(raw.slice(4, 6)),
    };
  };

  const parseRgbFunction = (value: string): { r: number; g: number; b: number; a?: number } | null => {
    const v = value.trim().toLowerCase();

    // Examples:
    // - rgb(0, 0, 0)
    // - rgba(255, 255, 255, 1)
    const match = v.match(/^rgba?\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})(?:\s*,\s*([0-9.]+)\s*)?\)$/);
    if (!match) return null;

    const r = parseInt(match[1], 10);
    const g = parseInt(match[2], 10);
    const b = parseInt(match[3], 10);
    const a = match[4] !== undefined ? Number(match[4]) : undefined;

    if ([r, g, b].some((n) => Number.isNaN(n) || n < 0 || n > 255)) return null;
    if (a !== undefined && (Number.isNaN(a) || a < 0 || a > 1)) return null;

    return { r, g, b, a };
  };

  const parseColorToRgb = (value: string): { r: number; g: number; b: number } | null => {
    const v = value.trim().toLowerCase();
    if (!v) return null;

    if (v === 'black') return { r: 0, g: 0, b: 0 };
    if (v === 'white') return { r: 255, g: 255, b: 255 };

    const hexParsed = parseHexColor(v);
    if (hexParsed) return hexParsed;

    const rgbParsed = parseRgbFunction(v);
    if (rgbParsed) return { r: rgbParsed.r, g: rgbParsed.g, b: rgbParsed.b };

    return null;
  };

  const isNearBlack = (rgb: { r: number; g: number; b: number }): boolean => rgb.r <= 5 && rgb.g <= 5 && rgb.b <= 5;
  const isNearWhite = (rgb: { r: number; g: number; b: number }): boolean =>
    rgb.r >= 250 && rgb.g >= 250 && rgb.b >= 250;

  const declarations = style
    .split(';')
    .map((d) => d.trim())
    .filter(Boolean);

  const safeValueRegex = /(expression\s*\(|url\s*\(|javascript\s*:|@import|behavior\s*:)/i;

  const kept: string[] = [];

  for (const decl of declarations) {
    const colonIdx = decl.indexOf(':');
    if (colonIdx === -1) continue;

    const prop = decl.slice(0, colonIdx).trim().toLowerCase();
    const value = decl.slice(colonIdx + 1).trim();

    if (!prop || !value) continue;

    // Remove MS Word specific styles and any property that isn't in our explicit allowlist.
    if (!ALLOWED_INLINE_STYLE_PROPERTIES.has(prop)) continue;

    if (safeValueRegex.test(value)) continue;

    // Normalize/remove "default" values to keep pasted HTML clean.
    if (prop === 'color') {
      const rgb = parseColorToRgb(value);
      if (rgb && isNearBlack(rgb)) continue; // default black -> strip
    }

    if (prop === 'background-color' || prop === 'background') {
      const rgb = parseColorToRgb(value);
      if (rgb && isNearWhite(rgb)) continue; // default white -> strip
    }

    if (prop === 'font-style') {
      const v = value.toLowerCase();
      if (v === 'normal') continue; // default -> strip
    }

    if (prop === 'text-align') {
      const v = value.toLowerCase();
      if (v !== 'center' && v !== 'right') continue; // default/justify/start/left -> strip
    }

    if (prop === 'font-weight') {
      const v = value.trim().toLowerCase();
      if (v === 'normal' || v === 'lighter') continue;
      if (v === 'bold' || v === 'bolder') {
        kept.push('font-weight:bold');
        continue;
      }

      const numeric = Number.parseInt(v, 10);
      if (Number.isFinite(numeric)) {
        if (numeric >= 600) {
          kept.push('font-weight:bold');
        }
        // < 600 => not bold => strip
        continue;
      }

      // Unknown keyword (medium/regular/etc) => strip to avoid carrying defaults.
      continue;
    }

    kept.push(`${prop}: ${value}`);
  }

  if (kept.length === 0) return '';
  return `${kept.join('; ')};`;
};

export interface ContentConversionConfig {
  allowedTags?: string[];
  allowedAttributes?: AllowedAttributes;
  classToStyleMap?: ClassToStyleMap;
  issuedAt?: string | null;
  additionalTransformations?: Array<(doc: Document) => Document>;
  preserveInsertNodes?: boolean; // When true, skip unwrapping insert nodes to preserve tracked changes
}

export default class ContentConversionHelper {
  /**
   * Merges user-provided config with default configuration.
   */
  private static getConfig(customConfig?: ContentConversionConfig): Required<ContentConversionConfig> {
    return {
      allowedTags: customConfig?.allowedTags || defaultAllowedTags,
      allowedAttributes: { ...defaultAllowedAttributes, ...(customConfig?.allowedAttributes || {}) },
      classToStyleMap: { ...defaultClassToStyleMap, ...(customConfig?.classToStyleMap || {}) },
      issuedAt: customConfig?.issuedAt || null,
      additionalTransformations: customConfig?.additionalTransformations || [],
      preserveInsertNodes: customConfig?.preserveInsertNodes || false,
    };
  }

  /**
   * Detects if HTML is already in editor format (has text-entity, PhxEditorTheme classes)
   * vs legacy format (has lexientity, MsoNormal classes)
   */
  static isEditorFormat(html: string): boolean {
    if (!html) return false;
    const editorMarkers = [
      'class="text-entity"',
      "class='text-entity'",
      'class="PhxEditorTheme__',
      "class='PhxEditorTheme__",
      'class="editor-insert"',
      'class="editor-delete"',
    ];
    return editorMarkers.some((marker) => html.includes(marker));
  }

  /**
   * Smart content loader that detects format and applies appropriate transformation.
   * For already-editor-format content, returns as-is (no transformation needed).
   * For legacy format, applies full conversion.
   *
   * Note: Track changes cleanup based on issuedAt is now handled at save time
   * via cleanupTrackChangesForStorage in documentTransformations.ts
   */
  static contentForEditorSmart(htmlString: string, config?: ContentConversionConfig): string {
    if (!htmlString) return '';

    // Already editor format - no transformation needed
    if (this.isEditorFormat(htmlString)) {
      return htmlString;
    }

    // Legacy format - full conversion
    return this.contentForEditor(htmlString, config);
  }

  /**
   * Converts <lexientity> elements to <span class="text-entity">
   * Preserves formatting from parent elements or data-format attribute
   */
  private static convertLexientityToTextEntity(doc: Document): void {
    doc.querySelectorAll('lexientity').forEach((lexientity) => {
      // Extract title attribute and create data object
      const title = lexientity.getAttribute('title');
      const description = lexientity.getAttribute('description');

      // Only include description if it exists
      const dataObj: { title?: string; description?: string } = title ? { title } : {};
      if (description) {
        dataObj.description = description;
      }

      // Create the span element
      const newSpan = doc.createElement('span');

      // Build class list starting with text-entity
      const classes = ['text-entity'];

      // Check for formatting from data-format attribute, style attribute, or parent elements
      const dataFormat = lexientity.getAttribute('data-format') || '';
      const styleAttr = lexientity.getAttribute('style') || '';
      const formatList = dataFormat.split(',').map((f) => f.trim());

      // Check for bold
      if (
        formatList.includes('bold') ||
        styleAttr.includes('font-weight:bold') ||
        styleAttr.includes('font-weight: bold') ||
        styleAttr.includes('font-weight:700') ||
        lexientity.closest('strong, b') !== null
      ) {
        classes.push('PhxEditorTheme__textBold');
      }

      // Check for italic
      if (
        formatList.includes('italic') ||
        styleAttr.includes('font-style:italic') ||
        styleAttr.includes('font-style: italic') ||
        lexientity.closest('em, i') !== null
      ) {
        classes.push('PhxEditorTheme__textItalic');
      }

      // Check for underline
      if (
        formatList.includes('underline') ||
        styleAttr.includes('text-decoration:underline') ||
        styleAttr.includes('text-decoration: underline') ||
        lexientity.closest('u') !== null
      ) {
        classes.push('PhxEditorTheme__textUnderline');
      }

      // Check for strikethrough
      if (
        formatList.includes('strikethrough') ||
        styleAttr.includes('text-decoration:line-through') ||
        styleAttr.includes('text-decoration: line-through') ||
        lexientity.closest('s, strike, del') !== null
      ) {
        classes.push('PhxEditorTheme__textStrikethrough');
      }

      newSpan.className = classes.join(' ');

      // Set the data attribute directly
      const jsonStr = JSON.stringify(dataObj);
      newSpan.setAttribute('data-textentity-data', jsonStr);

      // Set the title if it exists
      if (title) {
        newSpan.setAttribute('title', title);
      }

      // Set the text content
      newSpan.textContent = lexientity.textContent?.replace(/[\[\]]/g, '') || '';

      lexientity.replaceWith(newSpan);
    });
  }

  static contentForEditor(htmlString: string, config?: ContentConversionConfig): string {
    const { allowedTags, allowedAttributes } = this.getConfig(config);

    const doc = getSanitizedDocumentFromString(htmlString, createEditorSanitizeConfig(allowedTags, allowedAttributes));

    // Apply common cleanup operations (lexindex, images with dimensions preserved, S3 links)
    applyCommonCleanup(doc, true);

    // Convert lexientity tags to text-entity spans
    this.convertLexientityToTextEntity(doc);

    // Preserve lexical markup classes and attributes for contentEditable
    // Keep PhxEditorTheme__ classes, list styles, and other lexical-specific classes
    doc.querySelectorAll('*').forEach((element) => {
      if (element instanceof HTMLElement) {
        // Preserve PhxEditorTheme__ classes and other lexical-specific classes
        const classes = Array.from(element.classList);
        const preservedClasses = classes.filter(shouldPreserveClass);

        // Preserve list-related classes and data attributes
        if (element.tagName.toLowerCase() === 'ol' || element.tagName.toLowerCase() === 'ul') {
          const listType = element.getAttribute('data-list-type');
          if (listType) {
            element.setAttribute('data-list-type', listType);
            const style = element.getAttribute('style') || '';
            const hasListStyleType = /list-style-type\s*:/i.test(style);
            // Only strip the style attribute when it doesn't contain an explicit list-style-type.
            // For pasted custom markers (e.g. upper-roman) we rely on inline list-style-type, so
            // we must preserve it.
            if (!hasListStyleType) {
              element.removeAttribute('style');
            }
          }
        }

        // Update class attribute to only include preserved classes
        if (preservedClasses.length > 0) {
          element.className = preservedClasses.join(' ');
        } else {
          element.removeAttribute('class');
        }

        // Preserve style attributes for elements that need them
        if (element.hasAttribute('style')) {
          const style = element.getAttribute('style') || '';
          const filteredStyle = filterAllowedInlineStyleProperties(style);
          if (filteredStyle) {
            element.setAttribute('style', filteredStyle);
          } else {
            element.removeAttribute('style');
          }
        }
      }
    });

    const cleanUpIssuedTags = pipe(
      (doc: Document) => removeIssuedDeletedNodes(doc, config?.issuedAt),
      (doc: Document) => unWrapIssuedInsertedNodes(doc, config?.issuedAt),
    );
    syncDocumentContent(doc, cleanUpIssuedTags(doc));

    let finalHTML = doc.body.innerHTML.trim();

    // Ensure img tags with data-attachment-id are properly formatted and preserve all attributes
    finalHTML = finalHTML.replace(/<img([^>]*?)(\/?)>/g, (match, attributes, existingSlash) => {
      if (attributes.includes('data-attachment-id')) {
        // Extract all attributes and ensure they're preserved
        const attrRegex = /([a-zA-Z0-9\-_]+)(?:=(?:"([^"]*)"|'([^']*)'|([^\s>]*))|)/g;
        const attrs: Record<string, string> = {};
        let attrMatch;

        while ((attrMatch = attrRegex.exec(attributes)) !== null) {
          const [fullAttr, name, value1, value2, value3] = attrMatch;
          if (name) {
            attrs[name] = value1 || value2 || value3 || '';
          }
        }

        // Set src to empty string
        attrs['src'] = '';

        // Rebuild the tag with all preserved attributes
        const formattedAttrs = Object.entries(attrs)
          .map(([name, value]) => (value ? `${name}="${value}"` : name))
          .join(' ');

        return `<img ${formattedAttrs}/>`;
      }
      return match;
    });

    return removeLeadingEmptyParagraph(finalHTML);
  }

  /**
   * Processes list elements: only the root list has data-list-type; nested lists
   * are styled by the root's variant and their level relative to that root.
   */
  private static processListStyles(element: HTMLElement): void {
    const processList = (list: HTMLElement, rootListType: string, level: number): void => {
      const marker = getListMarker(rootListType, level);
      list.style.listStyleType = marker;
      // Only the root (level 0) carries data-list-type; nested lists do not.
      if (level > 0) {
        list.removeAttribute('data-list-type');
      }

      const listItems = Array.from(list.children).filter((child) => child.tagName.toLowerCase() === 'li');
      listItems.forEach((li) => {
        const liElement = li as HTMLElement;
        const nestedLists = Array.from(liElement.children).filter(
          (child) => child.tagName.toLowerCase() === 'ol' || child.tagName.toLowerCase() === 'ul',
        );

        const meaningfulChildren = Array.from(liElement.childNodes).filter((node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            return !!node.textContent && node.textContent.trim().length > 0;
          }
          if (node.nodeType === Node.COMMENT_NODE) {
            return false;
          }
          return true;
        });
        if (nestedLists.length === 1 && meaningfulChildren.length === 1 && meaningfulChildren[0] === nestedLists[0]) {
          liElement.style.listStyleType = 'none';
        }

        nestedLists.forEach((nestedList) => {
          if (nestedList instanceof HTMLElement) {
            processList(nestedList, rootListType, level + 1);
          }
        });
      });
    };

    const lists = Array.from(element.querySelectorAll('ol[data-list-type], ul[data-list-type]'));
    lists.forEach((list) => {
      if (!(list instanceof HTMLElement)) return;
      let parent = list.parentElement;
      let hasListAncestor = false;
      while (parent) {
        if (parent.tagName.toLowerCase() === 'ol' || parent.tagName.toLowerCase() === 'ul') {
          hasListAncestor = true;
          break;
        }
        parent = parent.parentElement;
      }
      if (hasListAncestor) return;

      const listType = list.getAttribute('data-list-type');
      if (!listType || !isValidListType(listType)) return;

      processList(list, listType, 0);
    });
  }

  /**
   * Normalizes list structure so that nested lists live inside the logical parent
   * list item instead of being wrapped in an extra sibling <li>.
   *
   * Example (what we get from the editor sometimes):
   *   <ol>
   *     <li>one</li>
   *     <li>two</li>
   *     <li><ol>...</ol></li>
   *   </ol>
   *
   * Becomes (for render only):
   *   <ol>
   *     <li>one</li>
   *     <li>two<ol>...</ol></li>
   *   </ol>
   */
  private static normalizeListStructureForRender(root: HTMLElement): void {
    const lists = Array.from(root.querySelectorAll('ol, ul'));

    lists.forEach((list) => {
      if (!(list instanceof HTMLElement)) return;

      const liChildren = Array.from(list.children).filter(
        (child) => child.tagName.toLowerCase() === 'li',
      ) as HTMLElement[];

      liChildren.forEach((li, index) => {
        if (index === 0) {
          return;
        }

        // Consider only non-whitespace child nodes
        const meaningfulChildren = Array.from(li.childNodes).filter((node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            return !!node.textContent && node.textContent.trim().length > 0;
          }
          // Ignore pure comments
          if (node.nodeType === Node.COMMENT_NODE) {
            return false;
          }
          return true;
        });

        if (meaningfulChildren.length !== 1) {
          return;
        }

        const onlyChild = meaningfulChildren[0];
        if (!(onlyChild instanceof HTMLElement) || !['ol', 'ul'].includes(onlyChild.tagName.toLowerCase())) {
          return;
        }

        const prevLi = liChildren[index - 1];
        if (!prevLi) {
          return;
        }

        // Move the nested list into the previous <li> and remove the wrapper <li>.
        prevLi.appendChild(onlyChild);
        li.remove();
      });
    });
  }

  /**
   * Converts PhxEditorTheme__ classes to inline styles for email/PDF rendering
   */
  private static convertClassesToInlineStyles(doc: Document): string {
    doc.querySelectorAll('[class]').forEach((element) => {
      if (!(element instanceof HTMLElement)) return;

      const classes = element.className.split(/\s+/);
      const styleObj: Record<string, string> = {};
      const keepClasses: string[] = [];

      classes.forEach((cls) => {
        if (defaultClassToStyleMap[cls]) {
          Object.assign(styleObj, defaultClassToStyleMap[cls]);
        } else if (cls === 'phx-cross-ref-link' || cls.startsWith('editor-')) {
          keepClasses.push(cls);
        } else if (!cls.startsWith('PhxEditorTheme__') && !cls.startsWith('phx-')) {
          keepClasses.push(cls);
        }
      });

      // Apply inline styles
      if (Object.keys(styleObj).length > 0) {
        const existingStyle = element.getAttribute('style') || '';
        const newStyles = Object.entries(styleObj)
          .map(([prop, val]) => {
            const kebabProp = prop.replace(/([A-Z])/g, '-$1').toLowerCase();
            return `${kebabProp}: ${val}`;
          })
          .join('; ');
        element.setAttribute('style', existingStyle ? `${existingStyle}; ${newStyles}` : newStyles);
      }

      // Update classes
      if (keepClasses.length > 0) {
        element.className = keepClasses.join(' ');
      } else {
        element.removeAttribute('class');
      }
    });

    return doc.body.innerHTML.trim();
  }

  /**
   * Converts <span class="text-entity"> elements back to <lexientity>
   * Wraps in formatting elements to preserve styling
   */
  private static convertTextEntityToLexientity(doc: Document): void {
    doc.querySelectorAll('span.text-entity').forEach((span) => {
      const lexientity = doc.createElement('lexientity');
      lexientity.textContent = `[${span.textContent}]`;

      // Extract data from data-textentity-data attribute
      const dataAttr = span.getAttribute('data-textentity-data');
      if (dataAttr) {
        try {
          const data = JSON.parse(dataAttr);
          if (data.title) {
            lexientity.setAttribute('title', data.title);
          }
        } catch (e) {
          // Silently ignore parse errors
        }
      }

      // Preserve formatting by wrapping in semantic elements (backends strip attributes but preserve elements)
      // Also check for parent formatting elements (Lexical may wrap in <em>, <strong>, etc.)
      const classList = span.classList;

      const hasBold = classList.contains('PhxEditorTheme__textBold') || span.closest('strong, b') !== null;
      const hasItalic = classList.contains('PhxEditorTheme__textItalic') || span.closest('em, i') !== null;
      const hasUnderline = classList.contains('PhxEditorTheme__textUnderline') || span.closest('u') !== null;
      const hasStrikethrough =
        classList.contains('PhxEditorTheme__textStrikethrough') || span.closest('s, strike, del') !== null;

      // Build the element chain: lexientity wrapped in formatting elements
      let resultElement: HTMLElement = lexientity;

      if (hasStrikethrough) {
        const s = doc.createElement('s');
        s.appendChild(resultElement);
        resultElement = s;
      }

      if (hasUnderline) {
        const u = doc.createElement('u');
        u.appendChild(resultElement);
        resultElement = u;
      }

      if (hasBold) {
        const strong = doc.createElement('strong');
        strong.appendChild(resultElement);
        resultElement = strong;
      }

      if (hasItalic) {
        const em = doc.createElement('em');
        em.appendChild(resultElement);
        resultElement = em;
      }

      span.replaceWith(resultElement);
    });
  }

  static contentForRender(editorHtmlString: string, config?: ContentConversionConfig): string {
    const { allowedTags, allowedAttributes, additionalTransformations } = this.getConfig(config);

    const doc = getSanitizedDocumentFromString(
      editorHtmlString,
      createRenderSanitizeConfig(allowedTags, allowedAttributes),
    );

    // Apply common cleanup operations (lexindex, images, S3 links)
    applyCommonCleanup(doc);

    // Convert text-entity spans back to lexientity elements
    this.convertTextEntityToLexientity(doc);

    // 5. Apply track changes transformations
    const applyAllAdditionalTransformations = pipe(
      (doc: Document) => removeIssuedDeletedNodes(doc, config?.issuedAt),
      ...(config?.preserveInsertNodes ? [] : [(doc: Document) => unWrapIssuedInsertedNodes(doc, config?.issuedAt)]),
      ...additionalTransformations,
    );
    syncDocumentContent(doc, applyAllAdditionalTransformations(doc));

    // 6. Normalize list structure and then process list styles
    this.normalizeListStructureForRender(doc.body);
    this.processListStyles(doc.body);

    // 7. Apply table-specific styles
    doc.querySelectorAll('table').forEach((table) => {
      if (table instanceof HTMLElement) {
        table.style.borderCollapse = 'collapse';
        table.style.borderSpacing = '0';
        table.style.margin = '30px 0';
        table.style.tableLayout = 'fixed';
        table.style.width = '100%';
      }
    });

    doc.querySelectorAll('td, th').forEach((cell) => {
      if (cell instanceof HTMLElement) {
        cell.style.border = '1px solid #bbb';
        cell.style.padding = '6px 8px';
        cell.style.verticalAlign = cell.getAttribute('valign') || 'top';
        cell.style.textAlign = cell.getAttribute('align') || 'left';
        cell.style.position = 'relative';
        cell.style.outline = 'none';
        cell.style.minWidth = '75px';

        const bgcolor = cell.getAttribute('bgcolor');
        if (bgcolor) {
          cell.style.backgroundColor = bgcolor;
          cell.removeAttribute('bgcolor');
        }
      }
    });

    // 8. Convert classes to inline styles and return HTML
    return removeLeadingEmptyParagraph(this.convertClassesToInlineStyles(doc));
  }

  static convertHtmlTableToLexicalTable(htmlTable: HTMLTableElement): TableNode {
    const tableNode = new TableNode();
    const rows = htmlTable.rows;

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const tableRowNode = new TableRowNode();

      for (let j = 0; j < row.cells.length; j++) {
        const cell = row.cells[j];
        const tableCellNode = new TableCellNode();

        // Handle header cells
        if (cell.tagName.toLowerCase() === 'th') {
          tableCellNode.toggleHeaderStyle(TableCellHeaderStates.ROW);
        }

        // Handle cell content
        const content = cell.textContent?.trim() || '';
        if (content) {
          const paragraphNode = new PhxParagraphNode();
          const textNode = new PhxTextNode(content);
          paragraphNode.append(textNode);
          tableCellNode.append(paragraphNode);
        } else {
          // Empty cells should still have a paragraph node
          tableCellNode.append(new PhxParagraphNode());
        }

        tableRowNode.append(tableCellNode);
      }

      tableNode.append(tableRowNode);
    }

    return tableNode;
  }
}

interface SafeHtmlParserProps {
  content?: string;
  className?: string;
}

const VOID_ELEMENTS = new Set(['br', 'img', 'hr', 'input', 'meta', 'link']);

const ALLOWED_TAGS = [
  'p',
  'div',
  'span',
  'br',
  'b',
  'i',
  'strong',
  'em',
  'u',
  's',
  'strike',
  'del',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'table',
  'thead',
  'tbody',
  'tr',
  'td',
  'th',
  'lexientity',
  'a',
];

const ALLOWED_ATTRS = ['class', 'href', 'id', 'title', 'data-username', 'data-created-at'];

export const SafeHtmlParser: React.FC<SafeHtmlParserProps> = ({ content, className }) => {
  if (!content) return null;

  const parseOptions: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (domNode.type === 'tag') {
        const { name, attribs, children } = domNode;

        if (!ALLOWED_TAGS.includes(name)) {
          return null;
        }

        const props: Record<string, any> = {};

        // Handle class names
        const classNames = attribs?.class
          ?.split(' ')
          // .filter((className) => !className.startsWith('editor-insert'))
          .join(' ');

        if (classNames) {
          props.className = classNames;
        }

        // Preserve href, id, and title for cross reference links
        if (attribs) {
          if (attribs.href) props.href = attribs.href;
          if (attribs.id) props.id = attribs.id;
          if (attribs.title) props.title = attribs.title;
        }

        if (VOID_ELEMENTS.has(name)) {
          return React.createElement(name, props);
        }

        return React.createElement(name, props, children ? domToReact(children as DOMNode[], parseOptions) : null);
      }

      if (domNode.type === 'text') {
        return domNode.data;
      }

      return null;
    },
  };

  try {
    const sanitizedHtml = DOMPurify.sanitize(content, {
      ALLOWED_TAGS,
      ALLOWED_ATTR: ALLOWED_ATTRS,
      RETURN_DOM_FRAGMENT: false,
      RETURN_DOM: false,
    });

    return <div className={className}>{parse(sanitizedHtml, parseOptions)}</div>;
  } catch (error) {
    console.error('Error parsing HTML content:', error);
    return <>{content}</>;
  }
};
